import Decimal from "decimal.js";

/**
 * Isolated Decimal constructor so NetKeep never mutates decimal.js global
 * configuration. 40 significant digits is far beyond any realistic sale.
 * Default rounding is ROUND_HALF_UP ("commercial" rounding: 0.005 → 0.01).
 */
export const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });
export type Dec = InstanceType<typeof D>;

export const ZERO: Dec = new D(0);
export const HUNDRED: Dec = new D(100);

/** Minor-unit digits per currency. Defaults to 2. */
const CURRENCY_DECIMALS: Record<string, number> = { JPY: 0, KRW: 0 };
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$",
  CAD: "CA$",
  AUD: "A$",
  GBP: "£",
  EUR: "€",
  JPY: "¥",
};

export class FeeEngineError extends Error {
  constructor(
    message: string,
    readonly field?: string,
  ) {
    super(message);
    this.name = "FeeEngineError";
  }
}

const DECIMAL_PATTERN = /^\d+(\.\d+)?$/;
const SIGNED_DECIMAL_PATTERN = /^-?\d+(\.\d+)?$/;

/** Parses a non-negative decimal string. Throws FeeEngineError on bad input. */
export function parseAmount(value: string | undefined, field: string, fallback = "0"): Dec {
  const raw = (value ?? fallback).trim();
  if (!DECIMAL_PATTERN.test(raw)) {
    throw new FeeEngineError(`${field} must be a non-negative decimal number, got "${value}"`, field);
  }
  return new D(raw);
}

/** Parses a decimal string that may be negative (e.g. a target profit). */
export function parseSigned(value: string, field: string): Dec {
  const raw = value.trim();
  if (!SIGNED_DECIMAL_PATTERN.test(raw)) {
    throw new FeeEngineError(`${field} must be a decimal number, got "${value}"`, field);
  }
  return new D(raw);
}

export function currencyDecimals(currency: string): number {
  return CURRENCY_DECIMALS[currency.toUpperCase()] ?? 2;
}

/** Rounds to the currency's minor unit using ROUND_HALF_UP. */
export function roundMoney(value: Dec, currency: string): Dec {
  return value.toDecimalPlaces(currencyDecimals(currency), D.ROUND_HALF_UP);
}

/** Serializes money with exactly the currency's minor-unit digits. */
export function moneyString(value: Dec, currency: string): string {
  return value.toFixed(currencyDecimals(currency), D.ROUND_HALF_UP);
}

/** Serializes a percentage rounded to 2 decimal places. */
export function percentString(value: Dec): string {
  return value.toFixed(2, D.ROUND_HALF_UP);
}

/** Formats money for human-readable explanations, e.g. "$1,234.50". */
export function formatMoney(value: Dec, currency: string): string {
  const fixed = moneyString(value.abs(), currency);
  const [whole = "0", frac] = fixed.split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const symbol = CURRENCY_SYMBOLS[currency.toUpperCase()] ?? `${currency.toUpperCase()} `;
  return `${value.isNegative() && !value.isZero() ? "−" : ""}${symbol}${grouped}${frac ? `.${frac}` : ""}`;
}

/** Formats a percent for explanations without trailing zeros, e.g. "13.6%". */
export function formatPercent(value: Dec): string {
  return `${value.toDecimalPlaces(4).toString()}%`;
}

export function minDec(a: Dec, b: Dec): Dec {
  return a.lessThan(b) ? a : b;
}

export function maxDec(a: Dec, b: Dec): Dec {
  return a.greaterThan(b) ? a : b;
}
